import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import ChannelList from './components/channelList'
import { selectPostalCode } from 'src/modules/reducers/market'
import { useSelector, useDispatch } from 'react-redux'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import {
  selectFuboFreeChannels,
  selectLatinoChannels,
  selectMostChannels,
  selectZeeFamilyChannels,
  selectCricketInternationalChannels,
  selectChannelsByRatePlanCode,
  selectFuboSportsChannels,
  selectChannelsByAddOnCode,
} from 'src/modules/reducers/products/selectors'
import {
  tryFetchProductByRatePlan,
  tryFetchAddonByRatePlan,
} from 'src/modules/reducers/products/actions/fetchProductByRatePlan'
import { ratePlanCodes } from 'src/constants'
import { laChannelCount } from 'src/constants/planData/latinoPlanData'
import { colors, spacing, mediaQuery, typographyMixin, heading } from 'src/constants/marketing-ds'
import CTAButton from '../CTAButtonNew'
import { SectionContainer, SectionHeader } from '../SharedComponents'

const ChannelSection = ({
  lang = 'en',
  market = 'us',
  showAllChannels = false,
  title,
  seeMoreText,
  initialChannelCount = 24,
  fuboFree = false,
  zeeFamily = false,
  ratePlanCode = false,
  sectionHeaderTitle,
  isFuboSports,
  channelsAddonCode,
}) => {
  const postal = useSelector(selectPostalCode)
  const dispatch = useDispatch()
  const getChannelType = () => {
    if (ratePlanCode) return 'ratePlanCode'
    if (market === 'latino') return 'latino'
    if (zeeFamily) return 'zeefamily'
    if (fuboFree) return 'fubofree'
    if (isFuboSports) return 'fubosports'
    return 'default'
  }

  const channelPlanMap = {
    latino: selectLatinoChannels,
    zeefamily: (state) => [
      ...selectCricketInternationalChannels(state),
      ...selectZeeFamilyChannels(state),
    ],
    ratePlanCode: (state) => [...selectChannelsByRatePlanCode(ratePlanCode)(state)],
    fubofree: selectFuboFreeChannels,
    fubosports: selectFuboSportsChannels,
    default: selectMostChannels,
  }

  const channelPlan = channelPlanMap[getChannelType()]

  const baseChannels = useSelector(channelPlan)

  // Always call the selector, but only with a value if channelsAddonCode exists
  const addonChannelsSelector = useMemo(
    () => (state) =>
      channelsAddonCode ? selectChannelsByAddOnCode(channelsAddonCode)(state) : [],
    [channelsAddonCode]
  )
  const addonChannels = useSelector(addonChannelsSelector)

  // Use useMemo to create stable combined channels
  const availableChannels = useMemo(() => {
    // If we have addon channels, merge them (dedupe by callSign)
    if (addonChannels.length > 0) {
      const channelMap = new Map()
      baseChannels.forEach((ch) => channelMap.set(ch.callSign, ch))
      addonChannels.forEach((ch) => channelMap.set(ch.callSign, ch))
      return Array.from(channelMap.values())
    }

    return baseChannels
  }, [baseChannels, addonChannels])

  // Filter out duplicate ESPN+ channels, keeping only the first one and renaming it to "ESPN+"
  const filteredChannels =
    availableChannels
      ?.filter((channel, index, array) => {
        if (channel.name && channel.name.includes('ESPN+')) {
          return array.findIndex((c) => c.name && c.name.includes('ESPN+')) === index
        }
        return true
      })
      .map((channel) => {
        if (channel.name && channel.name.includes('ESPN+')) {
          return { ...channel, name: 'ESPN+' }
        }
        return channel
      }) || []

  const [displayAllChannels, setDisplayAllChannels] = useState(showAllChannels)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const updatedChannels = filteredChannels?.length
    ? displayAllChannels
      ? filteredChannels
      : filteredChannels.slice(0, initialChannelCount)
    : []

  const latinoCopy =
    lang === 'es'
      ? `Accede a más de ${laChannelCount} canales premium`
      : `Get access to over ${laChannelCount}+ premium channels`

  let sectionTitleText =
    market === 'latino'
      ? latinoCopy
      : lang === 'en'
        ? isFuboSports
          ? `All channels available in ${postal}`
          : `See all channels available in ${postal}`
        : 'Corta el cable, quédate con los canales que te encantan'

  const moreChannelsText = seeMoreText || (lang === 'en' ? 'See more channels' : 'Ver más canales')

  const handleButtonClick = (event) => {
    event.preventDefault()
    setDisplayAllChannels(true)
    fireUIInteractionEvent(
      {
        element: moreChannelsText,
        component: 'channels_section_cta_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  useEffect(() => {
    if (zeeFamily) {
      dispatch(tryFetchProductByRatePlan(ratePlanCodes.zee_family_mo))
      dispatch(tryFetchAddonByRatePlan('us-recu-ga-intlsprt_crkt-mo-v1'))
    }
  }, [zeeFamily])

  useEffect(() => {
    if (ratePlanCode) {
      dispatch(tryFetchProductByRatePlan(ratePlanCode))
    }
  }, [ratePlanCode])

  useEffect(() => {
    if (
      availableChannels.length > 0 &&
      availableChannels.length <= initialChannelCount &&
      !displayAllChannels
    ) {
      setDisplayAllChannels(true)
    }
  }, [availableChannels, initialChannelCount, displayAllChannels])

  if (updatedChannels.length === 0) return null

  return (
    <SectionContainer backgroundColor={colors.slate[190]}>
      <StyledChannelSection id="channels-section">
        <SectionInner>
          <div className="channel-section-header">
            {sectionHeaderTitle && <SectionHeader title={sectionHeaderTitle} />}
            <SectionTitle>{title || sectionTitleText}</SectionTitle>
          </div>

          <ChannelListWrapper
            className={`${displayAllChannels ? 'expanded-channels' : 'closed-channels'} `}
          >
            <ChannelList channels={updatedChannels} activeCategory="all" />
          </ChannelListWrapper>
          {!displayAllChannels && (
            <SectionCTA onClick={handleButtonClick}>
              <CTAButton isOutlinedDark={true} icon="arrowDown">
                {moreChannelsText}
              </CTAButton>
            </SectionCTA>
          )}
        </SectionInner>
      </StyledChannelSection>
    </SectionContainer>
  )
}

export default ChannelSection

const StyledChannelSection = styled.section`
  padding: ${spacing.md} 0 ${spacing.xl};
  position: relative;
  z-index: 1;

  ${mediaQuery.tablet} {
    padding: ${spacing.lg} 0 ${spacing.xl};
  }

  ${mediaQuery.desktop} {
    padding: ${spacing.lg} 0 ${spacing.xl};
  }

  .closed-channels {
    &::after {
      display: block;
      position: absolute;
      bottom: 0px;
      background-image: linear-gradient(
        to bottom,
        rgba(250, 250, 250, 0) 0,
        rgba(250, 250, 250, 0.95) 95%,
        rgba(250, 250, 250, 1) 100%
      );
      content: '';
      height: 80px;
      width: 100%;
      z-index: 20;
    }
  }
`
const SectionInner = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${spacing.md};
  .channel-section-header {
    text-align: center;

    .section-title {
      margin-bottom: ${spacing.sm};
      ${mediaQuery.tablet} {
        margin-bottom: ${spacing.md};
      }
    }
  }
`
const SectionTitle = styled.h2`
  margin: 0;
  color: ${colors.semantic.text.inverse};
  text-align: center;
  ${typographyMixin(heading.xs)}

  ${mediaQuery.tablet} {
    ${typographyMixin(heading.sm)}
  }
`
const SectionCTA = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
`

const ChannelListWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: block;
  text-align: center;
  position: relative;
`
